import React, { useState, useRef, useEffect } from 'react';
import { User, Star, ChevronRight, ChevronLeft, Quote, Calendar } from 'lucide-react';

// Updated testimonials focused on health & wellness coaching
const testimonials = [
  {
    text: "I went from spending 15 hours a week searching for nutrition clients to letting the AI do it for me. Found 8 new wellness clients in my first month - more than the previous quarter combined.",
    author: "Rachel K.",
    role: "Nutritional Health Coach",
    rating: 5,
    highlight: "8 new clients in first month"
  },
  {
    text: "The social media scraping is a game-changer for my wellness practice. It finds people actively looking for fitness coaching that I would've completely missed. Conversion rates are 3x better than my previous marketing efforts.",
    author: "Marco T.",
    role: "Fitness & Wellness Coach",
    rating: 5,
    highlight: "3x better conversion rates"
  },
  {
    text: "The AI writes better personalized emails than I do. My response rate jumped from 8% to 26% - and these aren't just any leads, they're people genuinely interested in starting their wellness journey.",
    author: "Vivian L.",
    role: "Holistic Health Coach",
    rating: 5,
    highlight: "Response rate jumped to 26%"
  },
  {
    text: "As a yoga instructor who also offers wellness coaching, the hardest part was always finding quality leads. Now kinOS delivers 5-7 people weekly who are actively looking for exactly what I offer.",
    author: "Sean R.",
    role: "Yoga & Wellness Coach",
    rating: 5,
    highlight: "5-7 quality leads weekly"
  }
];

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const testimonialsRef = useRef(null);
  
  // Handle scroll of testimonial cards
  const handleScroll = () => {
    if (testimonialsRef.current) {
      const container = testimonialsRef.current;
      
      // Find which testimonial is most visible
      const testimonialElements = Array.from(container.querySelectorAll('.testimonial-card'));
      let closestTestimonial = 0;
      let closestDistance = Infinity;
      
      testimonialElements.forEach((element, index) => {
        const rect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const distance = Math.abs((rect.left + rect.width/2) - (containerRect.left + containerRect.width/2));
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestTestimonial = index;
        }
      });
      
      if (closestTestimonial !== activeIndex) {
        setActiveIndex(closestTestimonial);
      }
    }
  };
  
  // Add debounce to scroll handler
  useEffect(() => {
    const container = testimonialsRef.current;
    let scrollTimer;
    
    const handleScrollDebounced = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        handleScroll();
      }, 100);
    };
    
    if (container) {
      container.addEventListener('scroll', handleScrollDebounced);
      return () => {
        container.removeEventListener('scroll', handleScrollDebounced);
        clearTimeout(scrollTimer);
      };
    }
  }, [activeIndex]);
  
  // Scroll to selected testimonial
  const scrollToTestimonial = (index) => {
    setActiveIndex(index);
    
    if (testimonialsRef.current) {
      const testimonialElements = testimonialsRef.current.querySelectorAll('.testimonial-card');
      if (testimonialElements && testimonialElements[index]) {
        testimonialsRef.current.scrollTo({
          left: testimonialElements[index].offsetLeft - 16, // Account for container padding
          behavior: 'smooth'
        });
      }
    }
  };
  
  // Navigation controls
  const handlePrev = () => {
    const newIndex = Math.max(0, activeIndex - 1);
    scrollToTestimonial(newIndex);
  };
  
  const handleNext = () => {
    const newIndex = Math.min(testimonials.length - 1, activeIndex + 1);
    scrollToTestimonial(newIndex);
  };
  
  // Generate star rating display
  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <Star 
        key={i} 
        size={16} 
        className={`${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <section id="testimonials" className="py-12 md:py-20 bg-white">
      <div className="max-w-6xl mx-auto px-0 md:px-6">
        <div className="text-center mb-8 md:mb-12 px-4">
          <div className="inline-flex items-center mb-3 bg-[#c4ffa0]/20 px-3 py-1 rounded-full text-xs md:text-sm">
            <span className="mr-1 md:mr-2">⭐</span>
            <span className="text-[#2d4a1e] font-medium">CLIENT SUCCESS</span>
            <span className="ml-1 md:ml-2">⭐</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-gray-800 mb-3">What People Are Saying</h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
            Real coaches sharing real results with kinOS
          </p>
        </div>
        
        {/* Desktop navigation controls */}
        <div className="hidden md:flex justify-end mb-4 gap-2 px-4">
          <button 
            onClick={handlePrev}
            disabled={activeIndex === 0}
            className={`p-2 rounded-full border ${
              activeIndex === 0 
                ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                : 'border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={20} />
          </button>
          <button 
            onClick={handleNext}
            disabled={activeIndex === testimonials.length - 1}
            className={`p-2 rounded-full border ${
              activeIndex === testimonials.length - 1 
                ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                : 'border-gray-200 text-gray-600 hover:bg-gray-100'
            }`}
            aria-label="Next testimonial"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        
        {/* Full-width container for mobile */}
        <div className="w-full overflow-hidden">
          {/* Horizontally scrollable testimonial cards */}
          <div 
            ref={testimonialsRef}
            className="flex overflow-x-auto hide-scrollbar snap-x snap-mandatory pb-6 px-4 md:px-0"
          >
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className={`testimonial-card snap-center w-[calc(100%-32px)] md:w-auto sm:min-w-[60%] md:min-w-[40%] lg:min-w-0 lg:w-1/3 mr-4 md:px-3 flex-shrink-0 transition-all duration-500 ${
                  activeIndex === index ? 'scale-100 opacity-100' : 'scale-95 opacity-85'
                }`}
              >
                <div className="bg-white border border-gray-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 h-full flex flex-col min-h-[380px] md:min-h-[420px] overflow-hidden">
                  {/* Card header with rating */}
                  <div className="p-5 md:p-6 border-b border-gray-100 bg-[#c4ffa0]/10">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex">
                        {renderStars(testimonial.rating)}
                      </div>
                      <div className="text-[#2d4a1e] opacity-50">
                        <Quote size={24} />
                      </div>
                    </div>
                    <div className="inline-block bg-[#c4ffa0]/20 text-[#2d4a1e] px-3 py-1 rounded-full text-xs font-medium">
                      {testimonial.highlight}
                    </div>
                  </div>
                  
                  {/* Testimonial content */}
                  <div className="p-5 md:p-6 flex-grow flex flex-col justify-between">
                    <div>
                      <p className="text-gray-700 text-sm md:text-base italic leading-relaxed mb-6 break-words">
                        "{testimonial.text}"
                      </p>
                    </div>
                    
                    {/* Author info */}
                    <div className="flex items-center mt-auto">
                      <div className="w-10 h-10 rounded-full bg-[#c4ffa0]/20 flex items-center justify-center mr-3 flex-shrink-0">
                        <User className="w-5 h-5 text-[#2d4a1e]" />
                      </div>
                      <div className="min-w-0"> {/* Prevent text overflow */}
                        <p className="font-semibold text-gray-800 truncate">{testimonial.author}</p>
                        <p className="text-gray-500 text-xs md:text-sm truncate">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        
          {/* Mobile indicator dots */}
          <div className="flex justify-center mt-2 gap-2 md:hidden px-4">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToTestimonial(index)}
                className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
                  activeIndex === index ? 'bg-[#c4ffa0]' : 'bg-[#c4ffa0]/30'
                }`}
                aria-label={`View testimonial ${index + 1}`}
              ></button>
            ))}
          </div>
        </div>
        
        {/* CTA */}
        <div className="mt-10 md:mt-16 text-center">
          <a 
            href="https://calendly.com/usekinos"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <button className="bg-[#c4ffa0] text-[#2d4a1e] px-6 py-3 rounded-lg hover:bg-[#b3ee90] transition duration-300 shadow-md hover:shadow-lg flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              <span>Start Your Free 14-Day Trial</span>
            </button>
          </a>
        </div>
      </div>
      
      <style jsx>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default TestimonialsSection;